import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, MaskedLines } from "../components/Motion";
import { ParallaxHeroImage } from "../components/ParallaxImage";
import Seo from "../components/Seo";
import AnswerLead from "../components/AnswerLead";
import FaqSection from "../components/FaqSection";
import { IMAGES } from "../lib/images";
import { WINE_SOCIALS } from "../lib/socials";

const WineCard = ({ name, desc, url, testid, image, cta }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="group relative flex flex-col overflow-hidden border border-white/10 bg-obsidian-soft"
  >
    <div className="relative h-56 overflow-hidden">
      <img src={image} alt={name} className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian-soft to-transparent" />
    </div>
    <div className="flex flex-1 flex-col p-8">
      <h3 className="font-serif text-3xl font-light text-white transition-colors group-hover:text-champagne">{name}</h3>
      <p className="mt-4 flex-1 text-base font-light leading-relaxed text-white/60">{desc}</p>
      <span className="mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-champagne">
        {cta}
        <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </span>
    </div>
  </a>
);

const Wine = () => {
  const { t, lang } = useLang();
  const info = t.areas.wine;
  const wl = t.wineLinks;
  const followWine = { de: "Wine Pairing folgen", en: "Follow Wine Pairing", fr: "Suivre Wine Pairing" }[lang] || "Wine Pairing folgen";
  return (
    <div data-testid="wine-page" className="bg-obsidian">
      <Seo pageKey="wine" />
      <section className="relative flex min-h-[86vh] items-center overflow-hidden pt-24">
        <ParallaxHeroImage src={IMAGES.wine} alt={info.name}>
          <div className="absolute inset-0 bg-obsidian/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/60" />
        </ParallaxHeroImage>
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="text-xs uppercase tracking-[0.3em] text-champagne">
            {info.tag}
          </motion.span>
          <h1 className="mt-6 max-w-4xl font-serif text-6xl font-light leading-[0.95] tracking-tight text-white md:text-8xl">
            <MaskedLines lines={[info.name]} startDelay={0.3} />
          </h1>
          <Reveal delay={0.6}>
            <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-white/70 md:text-lg">{info.desc}</p>
          </Reveal>
        </div>
      </section>

      <AnswerLead pageKey="wine" />

      {/* Wine worlds */}
      <section className="border-t border-white/10 bg-obsidian py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-champagne">{wl.title}</span>
          </Reveal>
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            <Reveal>
              <WineCard
                name={wl.cuvee.name}
                desc={wl.cuvee.desc}
                url="https://myowncuvee.com/"
                testid="wine-external-link"
                image={IMAGES.wineCellar}
                cta={t.section.external}
              />
            </Reveal>
            <Reveal delay={0.1}>
              <WineCard
                name={wl.pairing.name}
                desc={wl.pairing.desc}
                url="https://wine-pairing.online/"
                testid="wine-pairing-link"
                image={IMAGES.champagne}
                cta={t.section.external}
              />
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <div className="mt-14 flex flex-wrap items-center gap-4 border-t border-white/10 pt-10" data-testid="wine-socials">
              <span className="text-[11px] uppercase tracking-[0.25em] text-white/45">{followWine}</span>
              <div className="flex flex-wrap gap-2.5">
                {WINE_SOCIALS.map(({ name, url, Icon }) => (
                  <a key={name} href={url} target="_blank" rel="noopener noreferrer" aria-label={`Wine Pairing ${name}`}
                    data-testid={`wine-social-${name.toLowerCase()}`}
                    className="flex h-10 w-10 items-center justify-center border border-white/15 text-white/55 transition-colors hover:border-champagne hover:text-champagne">
                    <Icon size={17} strokeWidth={1.6} />
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <FaqSection pageKey="wine" />
    </div>
  );
};

export default Wine;
